import React, { useState } from 'react';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Avatar,
    Menu,
    MenuItem,
    Divider,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Code as CodeIcon,
    Handshake as HandshakeIcon,
    Star as StarIcon,
    AccountCircle as AccountCircleIcon,
    Logout as LogoutIcon,
    CardGiftcard as CardGiftcardIcon,
    Groups as GroupsIcon,
    PersonAdd as PersonAddIcon,
    Settings as SettingsIcon,
    EmojiEmotions as EmojiEmotionsIcon,
    Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;
const closedDrawerWidth = 65; // Width when drawer is collapsed

const Layout = ({ children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(!isMobile);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, applicationModules, userRoles } = useAuth();

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    // Check if Giftcard Management (Application ID 1) is enabled
    const isGiftcardEnabled = applicationModules.some(
        app => app.ApplicationID === 1 && (app.SignedUp === true || app.SignedUp === 1)
    );

    // Check if Membership Platform (Application ID 2) is enabled
    const isMembershipEnabled = applicationModules.some(
        app => app.ApplicationID === 2 && (app.SignedUp === true || app.SignedUp === 1)
    );

    // Check if user has access to Module Setup (specific roles only)
    const allowedRoles = ['Support Super User', 'Administrator', 'Store Manager'];
    const hasSetupAccess = userRoles.some(role => allowedRoles.includes(role.RoleName));

    const menuSections = [
        {
            title: 'General',
            items: [
                {
                    text: 'Dashboard',
                    icon: <DashboardIcon />,
                    path: '/',
                },
            ]
        },
        // Only show Giftcard section if application ID 1 exists and is signed up
        ...(isGiftcardEnabled ? [{
            title: 'Giftcard',
            items: [
                {
                    text: 'Giftcard Management',
                    icon: <CardGiftcardIcon />,
                    path: '/giftcards',
                },
            ]
        }] : []),
        // Only show Membership section if application ID 2 exists and is signed up
        ...(isMembershipEnabled ? [{
            title: 'Membership',
            items: [
                {
                    text: 'Member List',
                    icon: <GroupsIcon />,
                    path: '/members',
                },
                {
                    text: 'Member Signup',
                    icon: <PersonAddIcon />,
                    path: '/member-signup',
                },
                {
                    text: 'Report',
                    icon: <AssessmentIcon />,
                    path: '/member-report',
                },
                // Only show Module Setup if user has required roles
                ...(hasSetupAccess ? [{
                    text: 'Module Setup',
                    icon: <SettingsIcon />,
                    path: '/membership-module-setup',
                }] : []),
            ]
        }] : []),
        // Other section - always visible
        // Hidden temporarily - will be shown in future
        // {
        //     title: 'Other',
        //     items: [
        //         {
        //             text: 'Icon Preview',
        //             icon: <EmojiEmotionsIcon />,
        //             path: '/icon-preview',
        //         },
        //     ]
        // },
    ];

    const handleNavigation = (path) => {
        navigate(path);
        if (isMobile) {
            setOpen(false);
        }
    };

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleProfileMenuClose();
        logout();
    };

    const drawer = (
        <Box sx={{ mt: isMobile ? 2 : 0 }}>
            {menuSections.map((section, sectionIndex) => (
                <Box key={section.title}>
                    {/* Section Title */}
                    {(isMobile || open) && (
                        <Typography
                            variant="overline"
                            sx={{
                                px: 2.5,
                                py: 1,
                                color: 'text.secondary',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                display: 'block',
                            }}
                        >
                            {section.title}
                        </Typography>
                    )}
                    
                    {/* Section Items */}
                    <List sx={{ py: 0 }}>
                        {section.items.map((item) => (
                            <ListItem key={item.text} disablePadding>
                                <ListItemButton
                                    selected={location.pathname === item.path}
                                    onClick={() => handleNavigation(item.path)}
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: isMobile ? 'flex-start' : (open ? 'initial' : 'center'),
                                        px: 2.5,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: isMobile ? 3 : (open ? 3 : 'auto'),
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.text} 
                                        sx={{ 
                                            opacity: isMobile || open ? 1 : 0,
                                            display: isMobile || open ? 'block' : 'none',
                                        }} 
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    
                    {/* Divider between sections (except for the last section) */}
                    {sectionIndex < menuSections.length - 1 && (isMobile || open) && (
                        <Divider sx={{ my: 1 }} />
                    )}
                </Box>
            ))}
        </Box>
    );

    return (
        <Box sx={{ 
            display: 'flex',
            height: '100vh',
            overflow: 'hidden'
        }}>
            <AppBar
                position="fixed"
                sx={{
                    width: '100%',
                    zIndex: theme.zIndex.drawer + 1,
                    height: { xs: '56px', sm: '64px' }, // Responsive height
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <HandshakeIcon sx={{ mr: 1, color: 'white' }} />
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Restaurant Operation Made Easy
                    </Typography>
                    
                    {/* User Profile Menu */}
                    {user && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: 'white', 
                                    mr: 1,
                                    display: { xs: 'none', sm: 'block' } // Hide on very small screens
                                }}
                            >
                                {user.name}
                            </Typography>
                            <IconButton
                                onClick={handleProfileMenuOpen}
                                sx={{ p: 0, color: 'white' }}
                            >
                                <Avatar
                                    src={user.picture}
                                    alt={user.name}
                                    sx={{ width: 32, height: 32 }}
                                />
                            </IconButton>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            {/* Navigation wrapper */}
            <Box
                component="nav"
                sx={{ 
                    width: { md: open ? drawerWidth : closedDrawerWidth }, 
                    flexShrink: { md: 0 }
                }}
            >
                {/* Mobile Drawer */}
                <Drawer
                    variant="temporary"
                    open={open}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' }, // Responsive height
                            top: { xs: '56px', sm: '64px' }, // Start below AppBar
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                
                {/* Desktop Drawer */}
                <Drawer
                    variant="permanent"
                    open={open}
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        width: open ? drawerWidth : closedDrawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            position: 'fixed',
                            width: open ? drawerWidth : closedDrawerWidth,
                            boxSizing: 'border-box',
                            height: 'calc(100% - 64px)', // Subtract AppBar height
                            top: '64px', // Start below AppBar
                            left: 0,
                            visibility: 'visible',
                            overflowX: 'hidden',
                            transition: theme.transitions.create(['width', 'margin'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            zIndex: theme.zIndex.drawer,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: '100%',
                    height: 'calc(100vh - 56px)', // Subtract AppBar height for mobile
                    height: { sm: 'calc(100vh - 64px)' }, // Subtract AppBar height for desktop
                    overflow: 'auto',
                    position: 'relative',
                    marginTop: { xs: '56px', sm: '64px' }, // Push content below AppBar
                    zIndex: 1
                }}
            >
                {children}
            </Box>

            {/* Profile Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                onClick={handleProfileMenuClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleProfileMenuClose} sx={{ py: 1.5 }}>
                    <Avatar src={user?.picture} sx={{ mr: 2 }} />
                    <Box>
                        <Typography variant="subtitle2">{user?.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user?.email}
                        </Typography>
                    </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Sign out
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default Layout;